import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

/**
 * `useState` que sobrevive ao recarregar: lê do `localStorage` na montagem e
 * grava a cada mudança.
 *
 * O `sanitize` roda na LEITURA e é obrigatório: o que está no storage pode ser
 * de uma versão antiga da tela, de outra conta no mesmo navegador ou editado à
 * mão — nunca deve chegar cru ao componente. Ele recebe o valor já parseado
 * (`unknown`) e devolve um valor válido; para descartar tudo, devolva o padrão.
 *
 * Storage indisponível (aba anônima, cota cheia) não quebra a tela: a leitura
 * cai no padrão e a gravação falha calada — a preferência só não persiste.
 *
 * @example
 * const [columns, setColumns] = usePersistedState('dash_columns', DEFAULT_COLUMNS, (raw) =>
 *   Array.isArray(raw) ? raw.filter(isColumnId) : DEFAULT_COLUMNS,
 * );
 */
export const usePersistedState = <T>(
  key: string,
  defaultValue: T,
  sanitize: (raw: unknown) => T,
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? defaultValue : sanitize(JSON.parse(stored));
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Sem storage a preferência vale só nesta sessão — não é erro da tela.
    }
  }, [key, value]);

  return [value, setValue];
};
